import { useAuth } from "@shared/hooks/useAuth";
import { PageLayout } from "@shared/layouts/PageLayout";
import { Skeleton } from "@mui/material";
import { CenteredLoadingSpinner } from "@shared/components/CenteredLoadingSpinner";
import { UserRoles } from "@store/auth.store";
import { roleIsAllowed } from "@shared/components/RoleAuthorized/roleIsAllowed";
import { NotFound } from "@pages/NotFound";
import { Navigate } from "react-router";
import { ChatSupport } from "@features/ChatSupport";
import { useSubscriptionLogic } from "@shared/hooks/useSubscriptionLogic";
import { useHasModule } from "@shared/access-control/useHasModule";
import type { SidekickModuleId } from "@shared/access-control/sidekick-modules";
import { useEffect, useMemo } from "react";
import { buildLoginRedirectUrl } from "./build-login-redirect-url";
import { consumeSsoLoginRedirect } from "@shared/utils/sso-login-redirect";

type RouteGuardProps = {
  children: React.ReactNode;
  /** Require authentication. Implied when roles is set. */
  protected?: boolean;
  /** Restrict to specific roles. Defaults to [UserRoles.User] when protected is true. */
  roles?: UserRoles[];
  /** Only render when unauthenticated. Redirects to / if authenticated. */
  guestOnly?: boolean;
  /** Element to render while auth is being validated. */
  fallback?: React.ReactElement;
  /** Restrict to accounts whose profile includes this module. Renders NotFound (not an access-denied page) when missing, so the route's existence isn't revealed. */
  requiredModule?: SidekickModuleId;
};

const DefaultFallback = () => <CenteredLoadingSpinner />;

export const RouteGuard = ({
  children,
  protected: isProtected,
  roles,
  guestOnly,
  fallback,
  requiredModule,
}: RouteGuardProps): React.ReactElement | null => {
  const { isAuthenticated, isValidatingCredentials, userRoles, error } =
    useAuth();
  const hasRequiredModule = useHasModule(requiredModule);

  const loginUrl = useMemo(
    () => buildLoginRedirectUrl(window.location.href),
    [],
  );

  const isGuarded = isProtected || !!roles;

  useSubscriptionLogic({
    isAuthenticated: isGuarded ? isAuthenticated : false,
  });

  useEffect(() => {
    if (!isGuarded || !isAuthenticated) return;
    const ssoRedirect = consumeSsoLoginRedirect();
    if (ssoRedirect) {
      window.location.replace(ssoRedirect);
    }
  }, [isGuarded, isAuthenticated]);

  // Guest-only route
  if (guestOnly) {
    if (isAuthenticated) return <Navigate to="/" />;
    if (isValidatingCredentials) return fallback ?? <DefaultFallback />;
    return <>{children}</>;
  }

  // Protected route
  if (isGuarded) {
    const requiredRoles = roles ?? [UserRoles.User];

    if (userRoles.length > 0 && !roleIsAllowed(requiredRoles, userRoles)) {
      return <NotFound />;
    }

    if (isAuthenticated && !hasRequiredModule) {
      return <NotFound />;
    }

    if (isAuthenticated) {
      return (
        <>
          {children}
          <ChatSupport />
        </>
      );
    }

    if (isValidatingCredentials) {
      return (
        <>
          {fallback ?? <DefaultFallback />}
          <ChatSupport />
        </>
      );
    }

    if (error?.type === "NETWORK_ERROR") {
      return (
        <PageLayout showFooter={false} disabled>
          <Skeleton variant="rounded" sx={{ height: 400 }} />
        </PageLayout>
      );
    }

    return <Navigate to={loginUrl} />;
  }

  // Public route
  return <>{children}</>;
};
